import { TITLE_LIST } from "./titleList";
import { getTitleForLevel } from "./levelUtils";

const SYSTEM_VOICE_RULES = [
  'You are "The System" inside a Solo Leveling-inspired productivity app.',
  "Maintain one consistent persona across every response.",
  "Tone: concise, clinical, directive, high-pressure but fair.",
  "Stay grounded in the real world. No monsters, magic, dungeons, supernatural powers, or fantasy roleplay.",
  "Use plain language first. Avoid jargon, corporate phrasing, or cryptic wording.",
  "Praise should be restrained. Criticism should be specific and actionable.",
  "Prefer measurable tasks, clear acceptance criteria, and short direct sentences.",
  "Do not use emojis unless explicitly requested.",
];

const formatSection = (title, lines = []) => {
  if (!lines.length) return [];
  return [title, ...lines.map((line) => `- ${line}`)];
};

const normalizeProfileLine = (value, maxLength = 180) =>
  String(value || "")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, maxLength);

export function getLevelDescriptor(level) {
  const numericLevel = Math.max(1, Number(level) || 1);
  return `Level ${numericLevel} (${getTitleForLevel(numericLevel)})`;
}

export function getLevelCatalogText() {
  return TITLE_LIST.map((entry) => `Level ${entry.level}: ${entry.title}`).join("\n");
}

export function buildMemoryContext(memoryObj) {
  if (!memoryObj) return "";

  const {
    allTimeXP = 0,
    allTimePerfectDays = 0,
    allTimePenalties = 0,
    longestStreakEver = 0,
    bossesDefeated = 0,
    bossesAbandoned = 0,
    firstQuestDate = "unknown",
    rankHistory = [],
    recentStreakBreaks = 0,
  } = memoryObj;

  const highestRank = rankHistory[rankHistory.length - 1] || "Unknown";

  return `Hunter record: Active since ${firstQuestDate}. All-time XP: ${allTimeXP}. Perfect days logged: ${allTimePerfectDays}. Longest streak: ${longestStreakEver} days. Bosses defeated: ${bossesDefeated}. Bosses abandoned: ${bossesAbandoned}. Penalty incidents: ${allTimePenalties}. Streak breaks in last 90 days: ${recentStreakBreaks}. Highest verified rank: ${highestRank}.`;
}

export function buildHunterProfileContext(profile) {
  if (!profile) return "";

  const profileSummary = normalizeProfileLine(profile.hunterProfile, 220);
  const longTermGoal = normalizeProfileLine(profile.longTermGoal, 200);
  const nonNegotiables = normalizeProfileLine(profile.nonNegotiables, 180);
  const frictionPoints = normalizeProfileLine(profile.frictionPoints, 180);
  const wakeTime = normalizeProfileLine(profile.wakeTime, 8);
  const sleepTime = normalizeProfileLine(profile.sleepTime, 8);
  const preferredFocusMinutes =
    Number.isFinite(Number(profile.preferredFocusMinutes)) && Number(profile.preferredFocusMinutes) > 0
      ? Number(profile.preferredFocusMinutes)
      : null;

  const parts = [];
  if (profileSummary) parts.push(`Hunter profile: ${profileSummary}.`);
  if (longTermGoal) parts.push(`Primary long-term objective: ${longTermGoal}.`);
  if (wakeTime) parts.push(`Usual wake target: ${wakeTime}.`);
  if (sleepTime) parts.push(`Usual sleep cutoff: ${sleepTime}.`);
  if (preferredFocusMinutes) parts.push(`Preferred focus block: ${preferredFocusMinutes} minutes.`);
  if (nonNegotiables) parts.push(`Non-negotiable rules: ${nonNegotiables}.`);
  if (frictionPoints) parts.push(`Known failure patterns: ${frictionPoints}.`);

  return parts.join(" ");
}

export function buildSystemPrompt({
  contextLines = [],
  instructionLines = [],
  outputLines = [],
  extraRules = [],
} = {}) {
  return [
    ...SYSTEM_VOICE_RULES,
    "",
    ...formatSection("Context:", contextLines),
    ...(contextLines.length ? [""] : []),
    ...formatSection("Instructions:", instructionLines),
    ...(instructionLines.length ? [""] : []),
    ...formatSection("Output requirements:", outputLines),
    ...(outputLines.length ? [""] : []),
    ...formatSection("Additional rules:", extraRules),
  ]
    .filter(Boolean)
    .join("\n");
}
